import React, { useState, useEffect, useRef } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { ShieldCheck, Eye, EyeOff } from 'lucide-react';

const makeChallenge = () => {
  const a = Math.floor(Math.random() * 9) + 2; // 2..10
  const b = Math.floor(Math.random() * 9) + 2;
  const ops = ['+', '-', '*'];
  const op = ops[Math.floor(Math.random() * ops.length)];
  let answer;
  if (op === '+') answer = a + b;
  else if (op === '-') answer = a > b ? a - b : b - a;
  else answer = a * b;
  const left = op === '-' && b > a ? b : a;
  const right = op === '-' && b > a ? a : b;
  return { left, right, op, answer };
};

const AdminLogin = ({ onLogin }) => {
  const { t, language } = useLanguage();
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [captchaInput, setCaptchaInput] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [challenge, setChallenge] = useState(makeChallenge());
  // Honeypot field (hidden from real users, bots will fill it)
  const honeypotRef = useRef(null);
  // Track mount time for time-based bot detection
  const mountTime = useRef(Date.now());

  useEffect(() => {
    setChallenge(makeChallenge());
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Honeypot check
    if (honeypotRef.current && honeypotRef.current.value) {
      setError(language === 'fi' ? 'Vahvistus epäonnistui.' : 'Verification failed.');
      return;
    }
    // Time-based check (humans take at least 1 second)
    if (Date.now() - mountTime.current < 1500) {
      setError(language === 'fi' ? 'Hetkinen, yritä uudelleen.' : 'Please wait a moment and try again.');
      return;
    }
    // Captcha check
    if (parseInt(captchaInput, 10) !== challenge.answer) {
      setError(language === 'fi' ? 'Väärä vastaus laskuun. Yritä uudelleen.' : 'Wrong answer to the math question. Try again.');
      setChallenge(makeChallenge());
      setCaptchaInput('');
      return;
    }

    setLoading(true);
    const trimmedPassword = password.trim();
    const ok = await onLogin(trimmedPassword);
    setLoading(false);
    if (!ok) {
      setError(t.admin.wrongPassword);
      setPassword('');
      setChallenge(makeChallenge());
      setCaptchaInput('');
    }
  };

  const opSymbol = challenge.op === '*' ? '×' : challenge.op;

  return (
    <div className="max-w-md mx-auto mt-16">
      <div className="bg-white rounded-2xl p-10 shadow-lg border border-[#EDE4D2]">
        <div className="flex justify-center mb-6">
          <img src="/logo.svg" alt="Ravintola La Maria" className="h-16 w-auto" />
        </div>
        <h1 className="font-serif text-2xl text-center text-[#1F2A24] mb-2 font-semibold">{t.admin.title}</h1>
        <p className="text-center text-[#6B6257] text-sm mb-8">Ravintola La Maria</p>

        <form onSubmit={handleSubmit} className="space-y-5" autoComplete="off">
          <div>
            <label className="block text-sm font-medium text-[#1F2A24] mb-2">{t.admin.password}</label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => { setPassword(e.target.value); setError(''); }}
                className="w-full px-4 py-3 pr-12 rounded-lg border border-[#E8DFCF] focus:outline-none focus:ring-2 focus:ring-[#B8934A]/40 focus:border-[#B8934A] transition-colors bg-[#FAF6F0] font-mono"
                placeholder="••••••••"
                autoFocus
                autoCapitalize="off"
                autoCorrect="off"
                spellCheck="false"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[#6B6257] hover:text-[#1F2A24] p-1"
                tabIndex={-1}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
            {password && (
              <p className="text-[10px] text-[#6B6257] mt-1.5 font-mono">
                {password.length} chars · {password.endsWith('!') ? '✓ ends with !' : '⚠ should end with !'}
              </p>
            )}
          </div>

          {/* Honeypot — hidden from real users via off-screen positioning */}
          <input
            ref={honeypotRef}
            type="text"
            name="website"
            tabIndex={-1}
            autoComplete="off"
            style={{ position: 'absolute', left: '-9999px', width: 1, height: 1, opacity: 0 }}
            aria-hidden="true"
          />

          {/* Math captcha */}
          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-[#1F2A24] mb-2">
              <ShieldCheck size={16} className="text-[#7A1F25]" />
              {language === 'fi' ? 'Vahvista että olet ihminen' : 'Verify you are human'}
            </label>
            <div className="flex items-center gap-3">
              <div className="px-4 py-3 rounded-lg border border-[#E8DFCF] bg-[#F0E9DC] font-serif text-lg font-semibold text-[#1F2A24] tabular-nums select-none">
                {challenge.left} {opSymbol} {challenge.right} = ?
              </div>
              <input
                type="text"
                inputMode="numeric"
                value={captchaInput}
                onChange={(e) => { setCaptchaInput(e.target.value); setError(''); }}
                className="flex-1 w-full px-4 py-3 rounded-lg border border-[#E8DFCF] focus:outline-none focus:ring-2 focus:ring-[#B8934A]/40 focus:border-[#B8934A] bg-[#FAF6F0] text-center text-lg tabular-nums"
                placeholder="?"
              />
            </div>
          </div>

          {error && <p className="text-red-600 text-sm">{error}</p>}

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-[#7A1F25] hover:bg-[#5A1219] disabled:opacity-60 text-white py-3 rounded-full font-medium transition-colors"
          >
            {loading ? '…' : t.admin.login}
          </button>
        </form>
      </div>
    </div>
  );
};

export default AdminLogin;
